import React, { FunctionComponent, useMemo } from 'react';
import ResumeSection, { BodyResumeSection } from '../ResumeSection';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import { PetType } from '@spike/model';
import Service from 'model/Service';
import useOrganizationMarketplaces from 'hooks/useOrganizationMarketplaces';

interface Props {
    petTypes: Array<PetType>;
    onEdit: () => void;
    service?: Service;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        container: {
            width: '100%',
            display: 'flex',
            flexDirection: 'column'
        },
        sectionContainer: {
            width: '100%'
        },
        locationsSectionContainer: {
            width: '100%',
            marginTop: 24,

            [theme.breakpoints.down('sm')]: {
                marginTop: 20
            }
        },
        titleContainer: {},
        title: {
            fontSize: 14,
            lineHeight: 1,
            textTransform: 'uppercase',

            [theme.breakpoints.down('sm')]: {
                fontSize: '14px',
                color: '#7A7A7A',
                fontWeight: 400,
                textTransform: 'unset'
            }
        },
        infoContainer: {
            width: '100%',
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center'
        },
        info: {
            fontSize: 16,
            lineHeight: 1,
            fontWeight: 500,
            marginTop: 8
        }
    })
);

export const ResumeCategory: FunctionComponent<Props> = props => {
    const classes = useStyles();
    const { organizationMarketplaces } = useOrganizationMarketplaces();

    const selectedLocations = useMemo(() => {
        const marketplaceIds =
            (props.service?.marketplaces ?? []).length > 0
                ? (props.service?.marketplaces ?? []).map(marketplace => marketplace.marketplaceId)
                : props.service?.marketplaceId != null
                ? [props.service.marketplaceId]
                : [];

        return marketplaceIds
            .map(marketplaceId => organizationMarketplaces.find(om => om.id === marketplaceId)?.name ?? null)
            .filter((name): name is string => name !== null)
            .sort((left, right) => left.toLocaleLowerCase().localeCompare(right.toLocaleLowerCase()));
    }, [organizationMarketplaces, props.service?.marketplaces, props.service?.marketplaceId]);

    const renderListedItems = (items: Array<string>) =>
        items.map((item, index) => {
            const postfix =
                items.length === 1 || items.length - 1 === index
                    ? ''
                    : index === items.length - 2
                    ? ' &\u00a0'
                    : ',\u00a0';

            return (
                <Typography
                    key={item}
                    className={classes.info}
                >
                    {item + postfix}
                </Typography>
            );
        });

    const petTypes = renderListedItems(props.petTypes.map(petType => petType.plural));
    const locations = renderListedItems(selectedLocations);

    return (
        <ResumeSection
            title="Category"
            service={props.service}
            {...props}
        >
            <BodyResumeSection>
                <Box className={classes.container}>
                    <Box className={classes.sectionContainer}>
                        <Box className={classes.titleContainer}>
                            <Typography className={classes.title}>Pet Catergory</Typography>
                        </Box>
                        <Box className={classes.infoContainer}>{petTypes}</Box>
                    </Box>
                    {selectedLocations.length > 0 && (
                        <Box className={classes.locationsSectionContainer}>
                            <Box className={classes.titleContainer}>
                                <Typography className={classes.title}>Locations</Typography>
                            </Box>
                            <Box className={classes.infoContainer}>{locations}</Box>
                        </Box>
                    )}
                </Box>
            </BodyResumeSection>
        </ResumeSection>
    );
};

export default ResumeCategory;
